import { FaceSmileIcon } from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";
import { ReactionHint } from "./ReactionHint";
import { RecentReaction } from "../../gql/graphql";
import { classNames, getISOWeekFromString } from "../_helpers";
import { readRecentReactionsQuery } from "../_lib/graphql/queries";

const formatReactionDate = (dateInput: string) => {
  const today = new Date()
  const date = new Date(dateInput)
  today.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)

  const daysAgo = Math.round((today.getTime() - date.getTime()) / 86400000)

  if (daysAgo === 0) return 'Heute'
  if (daysAgo === 1) return 'Gestern'
  if (daysAgo === 2) return 'Vorgestern'
  if (daysAgo > 2 && daysAgo <= 7) return date.toLocaleDateString('de-DE', { weekday: 'long' })

  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export const ReactionTicker = () => {
  const navigate = useNavigate()
  const [{ data }, reexecuteQuery] = useQuery({
    query: readRecentReactionsQuery,
    requestPolicy: 'cache-and-network',
  })
  const reactions = (data?.readRecentReactions || []).filter(Boolean) as RecentReaction[]
  const unseenCount = reactions.filter(reaction => !reaction.seen).length
  const wasOpen = useRef(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Beim Öffnen aktualisieren (als gesehen gilt erst, wenn die Ergebnisseite angesehen wurde)
  const handleOpenChange = (open: boolean) => {
    if (open && !wasOpen.current) {
      reexecuteQuery({ requestPolicy: 'network-only' })
    }
    wasOpen.current = open
  }

  // Auf neue Reaktionen prüfen, wenn die App wieder in den Vordergrund kommt
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        reexecuteQuery({ requestPolicy: 'network-only' })
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [reexecuteQuery])

  const goToResults = (reaction: RecentReaction) => {
    if (!reaction.workoutExecutionID || !reaction.date) {
      return
    }
    const date = new Date(reaction.date)
    navigate(`/calender/${date.getFullYear()}/${getISOWeekFromString(reaction.date)}/${date.getDay() || 7}/${reaction.workoutExecutionID}/results`)
  }

  return (
    <>
    <Menu>
      {({ open }) => (
        <OpenWatcher open={open} onChange={handleOpenChange}>
          <MenuButton
            ref={buttonRef}
            aria-label={'Neue Reaktionen'}
            className={'relative flex items-center justify-center w-10 h-10 rounded-full bg-neutral-800 ring-1 ring-neutral-700 text-neutral-400'}
          >
            <FaceSmileIcon className={'w-6 h-6'}/>
            {unseenCount > 0 && (
              <span className={'absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-[18px] text-center'}>
                {unseenCount > 9 ? '9+' : unseenCount}
              </span>
            )}
          </MenuButton>
          <MenuItems
            transition
            anchor="bottom end"
            className="w-72 max-h-[60vh] overflow-y-auto origin-top-right mt-4 z-[81] divide-y divide-neutral-400/30 rounded-lg border border-neutral-400/30 bg-black/60 backdrop-blur p-1 text-sm/6 text-white transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            {reactions.length === 0 ? (
              <div className={'py-3 px-3 text-neutral-400 text-center'}>Noch keine Reaktionen</div>
            ) : reactions.map(reaction => (
              <MenuItem key={reaction.id}>
                <button
                  onClick={() => goToResults(reaction)}
                  className={classNames(
                    'flex w-full items-center gap-3 py-2 px-3 text-left data-[focus]:bg-white/10',
                    !reaction.seen ? 'bg-sky-500/15 border-l-2 border-sky-400' : 'opacity-70'
                  )}
                >
                  <span className={'text-2xl leading-none'}>{reaction.emoji}</span>
                  <span className={'flex flex-col min-w-0'}>
                    <span className={'truncate'}>{reaction.memberName}</span>
                    <span className={'truncate text-xs text-neutral-400'}>
                      {reaction.date && formatReactionDate(reaction.date)}
                      {reaction.workoutTitle && ` · ${reaction.workoutTitle}`}
                    </span>
                    {reaction.score && (
                      <span className={'truncate text-xs text-neutral-300'}>Ergebnis: {reaction.score}</span>
                    )}
                  </span>
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </OpenWatcher>
      )}
    </Menu>
    <ReactionHint target={buttonRef}/>
    </>
  )
}

const OpenWatcher = ({ open, onChange, children }: { open: boolean, onChange: (open: boolean) => void, children: React.ReactNode }) => {
  useEffect(() => {
    onChange(open)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return <>{children}</>
}
